import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from 'react';
import { IMatterRequest } from '../interface/matterRequest';

interface ManageMatterRequestContextType {
  matterRequest: IMatterRequest | null;
  initialManageMatterRequest: IMatterRequest | null;
  updateManageMatterRequest: (
    matterRequest: IMatterRequest | null,
    shouldPrefillInitialMatterRequest?: boolean,
  ) => void;
  clearInitialManageMatterRequest: () => void;
}

const ManageMatterRequestContext = createContext<ManageMatterRequestContextType | undefined>(
  undefined,
);

export const ManageMatterRequestProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [matterRequest, setMatterRequest] = useState<IMatterRequest | null>(null);
  const [initialManageMatterRequest, setInitialManageMatterRequest] =
    useState<IMatterRequest | null>(null);

  const updateManageMatterRequest = useCallback(
    (data: IMatterRequest | null, shouldPrefillInitialMatterRequest?: boolean) => {
      if (shouldPrefillInitialMatterRequest) {
        setInitialManageMatterRequest(data);
      }
      setMatterRequest(data);
    },
    [],
  );
  const clearInitialManageMatterRequest = useCallback(() => {
    setInitialManageMatterRequest(null);
  }, []);
  const manageMatterRequestContextValue = useMemo(
    () => ({
      matterRequest,
      updateManageMatterRequest,
      initialManageMatterRequest,
      clearInitialManageMatterRequest,
    }),
    [
      matterRequest,
      updateManageMatterRequest,
      initialManageMatterRequest,
      clearInitialManageMatterRequest,
    ],
  );
  return (
    <ManageMatterRequestContext.Provider value={manageMatterRequestContextValue}>
      {children}
    </ManageMatterRequestContext.Provider>
  );
};

export const useManageMatterRequestContext = () => {
  const context = useContext(ManageMatterRequestContext);
  if (!context) {
    throw new Error(
      'useManageMatterRequestContext must be used within a ManageMatterRequestProvider',
    );
  }
  return context;
};
